// Utility Helper Functions for MedFast CRM Pro

import { facilities, wardsByFacility, medicationAlerts, rolePermissions } from '../data/mockData';

// Demo login (prototype only; not production auth)
export const REQUIRED_ADMIN_USERNAME = 'admin';
export const REQUIRED_ADMIN_PASSWORD = 'GreenApple@26!';

// Format currency for Australian dollars
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount);
};

// Format date for Australian locale
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format time for Australian locale
export const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get time ago string
export const getTimeAgo = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diff = Math.floor((now - past) / 1000);
  
  if (diff < 60) return `${diff} seconds ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
};

// Get current facility information
export const getCurrentFacility = (selectedFacility) => {
  const facility = facilities.find(f => f.id === selectedFacility);
  if (!facility) return { name: 'Loading...', tagline: 'Connecting to MedFastCRM Network...' };
  return {
    ...facility,
    tagline: 'Better medication outcomes for residents. Every day. Every time.',
    abn: '12 345 678 901',
    activeOrders: 23,
    expiringPrescriptions: 15,
    pendingRefills: 8,
    criticalAlerts: 3,
    monthlyMedicationBudget: 45000,
    currentMonthSpend: 32450,
    networkConnections: { pharmacies: 6, clinicians: 24, familyMembers: 142 },
    complianceScore: 98.5,
    incidentReduction: 76,
    timesSaved: '4.5 hours/day',
    errorReduction: '89%'
  };
};

// Get wards by facility
export const getWardsByFacility = (facilityId) => {
  return wardsByFacility[facilityId] || [];
};

// Authentication helper — single demo account (prototype only; not production auth)
export const authenticateUser = (username, password) => {
  if (!username || password !== REQUIRED_ADMIN_PASSWORD) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Auth] authenticateUser: missing username or wrong password');
    }
    return null;
  }
  const u = String(username).trim().toLowerCase();
  if (u !== REQUIRED_ADMIN_USERNAME) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Auth] authenticateUser: username not allowed', { username: u });
    }
    return null;
  }

  const now = new Date().toISOString();
  return {
    id: 'admin-001',
    name: 'Admin User',
    username: REQUIRED_ADMIN_USERNAME,
    role: 'admin',
    title: 'System Administrator',
    email: 'admin@medfastcrm.com',
    phone: '+61 400 123 456',
    facilities: ['all'],
    permissions: rolePermissions.admin?.permissions || ['all'],
    avatar: '👨\u200d💼',
    lastLogin: now,
    isActive: true,
  };
};

// Permission checking
export const hasPermission = (user, permission) => {
  if (!user) return false;
  return user.permissions.includes(permission) || user.permissions.includes('view_all');
};

export const canAccessFacility = (user, facilityId) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.role === 'nurse' || user.role === 'manager') return user.facility === facilityId;
  if (user.facilities) return user.facilities.includes(facilityId) || user.facilities.includes('all');
  return false;
};

export const canViewResident = (user, resident) => {
  if (!user) return false;
  if (user.role === 'admin' || user.role === 'doctor' || user.role === 'pharmacist') return true;
  if (user.role === 'nurse') {
    return resident.ward === user.ward && resident.facility === user.facility;
  }
  if (user.role === 'family') {
    return resident.id === user.residentId;
  }
  return false;
};

// Filter functions
export const getFilteredResidents = (selectedFacility, selectedWard, searchQuery, currentUser) => {
  // Import residents and facility helpers from mockData
  const { residents } = require('../data/mockData');
  
  let filtered = residents;
  
  // Filter by facility
  if (selectedFacility !== 'all') {
    filtered = filtered.filter(r => r.facility === selectedFacility);
  }
  
  if (currentUser?.role === 'nurse') {
    filtered = filtered.filter(resident => resident.ward === currentUser.ward);
  }
  if (currentUser?.role === 'family') {
    filtered = filtered.filter(resident => resident.id === currentUser.residentId);
  }
  if (selectedWard !== 'all') {
    filtered = filtered.filter(resident => resident.ward === selectedWard);
  }
  if (searchQuery) {
    filtered = filtered.filter(resident =>
      resident.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resident.room.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (resident.primaryDiagnosis && resident.primaryDiagnosis.some(condition =>
        condition.toLowerCase().includes(searchQuery.toLowerCase())
      ))
    );
  }
  return filtered;
};

export const getFilteredAlerts = (selectedFacility, alertFilter, currentUser) => {
  let filtered = medicationAlerts.filter(alert => alert.facility === selectedFacility);
  
  if (currentUser?.role === 'nurse') {
    const nurseWard = wardsByFacility[selectedFacility]?.find(w => w.id === currentUser.ward);
    if (nurseWard) {
      filtered = filtered.filter(alert => alert.ward === nurseWard.name);
    }
  }
  if (alertFilter !== 'all') {
    filtered = filtered.filter(alert => alert.type === alertFilter);
  }
  return filtered.sort((a, b) => {
    const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
};

// Get available tabs based on user role
export const getAvailableTabs = (userRole, facilityType = null) => {
  // Determine labels - Aged Care only
  const getLabel = (tabId) => {
    const labels = {
      dashboard: 'Dashboard',
      residents: 'Residents',
      enquiries: 'Enquiries',
      clinical: 'Clinical',
      medication: 'Medication',
      careplans: 'Care Plans',
      wellbeing: 'Wellbeing',
      telehealth: 'Telehealth',
      orders: 'Orders',
      quality: 'Quality',
      roster: 'Roster',
      billing: 'Billing',
      reports: 'Reports',
      network: 'Network',
      alerts: 'Alerts',
      kitchen: 'Kitchen',
      maintenance: 'Maintenance',
      emergency: 'Emergency',
      visitors: 'Visitors',
      wellbeing: 'Wellbeing',
      familyportal: 'Family Portal',
      mobileapp: 'Mobile App',
      hr: 'HR & Workforce',
      workforce: 'Enterprise Roster',
      settings: 'Settings'
    };
    return labels[tabId] || tabId;
  };

  const allTabs = [
    { id: 'dashboard', icon: 'LayoutDashboard', label: getLabel('dashboard') },
    { id: 'residents', icon: 'HeartHandshake', label: getLabel('residents') },
    { id: 'enquiries', icon: 'UserRoundPlus', label: getLabel('enquiries') },
    { id: 'clinical', icon: 'Stethoscope', label: getLabel('clinical') },
    { id: 'medication', icon: 'Pill', label: getLabel('medication') },
    { id: 'careplans', icon: 'FileHeart', label: getLabel('careplans') },
    { id: 'wellbeing', icon: 'Sparkles', label: getLabel('wellbeing') },
    { id: 'telehealth', icon: 'Video', label: getLabel('telehealth') },
    { id: 'orders', icon: 'PackageCheck', label: getLabel('orders') },
    { id: 'quality', icon: 'ShieldCheck', label: getLabel('quality') },
    { id: 'roster', icon: 'CalendarDays', label: getLabel('roster') },
    { id: 'billing', icon: 'Wallet', label: getLabel('billing') },
    { id: 'reports', icon: 'TrendingUp', label: getLabel('reports') },
    { id: 'network', icon: 'Globe', label: getLabel('network') },
    { id: 'alerts', icon: 'BellRing', label: getLabel('alerts') },
    { id: 'kitchen', icon: 'ChefHat', label: getLabel('kitchen') },
    { id: 'maintenance', icon: 'Wrench', label: getLabel('maintenance') },
    { id: 'emergency', icon: 'ShieldAlert', label: getLabel('emergency') },
    { id: 'visitors', icon: 'UsersRound', label: getLabel('visitors') },
    { id: 'familyportal', icon: 'HeartPulse', label: getLabel('familyportal') },
    { id: 'mobileapp', icon: 'Smartphone', label: getLabel('mobileapp') },
    { id: 'hr', icon: 'Award', label: getLabel('hr') },
    { id: 'workforce', icon: 'Building2', label: getLabel('workforce') },
    { id: 'settings', icon: 'SlidersHorizontal', label: getLabel('settings') }
  ];

  // Import rolePermissions from mockData
  const { rolePermissions } = require('../data/mockData');
  
  if (rolePermissions[userRole]) {
    return allTabs.filter(tab => 
      rolePermissions[userRole].tabs.includes(tab.id)
    );
  }
  
  return allTabs;
};

// Check if user has specific permission
export const hasRolePermission = (user, permission) => {
  if (!user || !user.permissions) return false;
  return user.permissions.includes(permission);
};

// Get role information
export const getRoleInfo = (userRole) => {
  const { rolePermissions } = require('../data/mockData');
  return rolePermissions[userRole] || null;
};

// Check if user can perform specific action
export const canPerformAction = (user, action) => {
  if (!user) return false;
  
  const actionPermissions = {
    'view_all_residents': ['view_all', 'view_all_patients'],
    'edit_resident': ['view_all', 'manage_users'],
    'prescribe_medication': ['edit_prescriptions', 'eprescribe'],
    'administer_medication': ['administer_medications'],
    'view_medical_history': ['view_medical_history', 'view_all_patients'],
    'add_clinical_notes': ['add_notes'],
    'manage_care_plans': ['approve_care_plans'],
    'manage_staff': ['manage_staff', 'manage_users'],
    'view_reports': ['view_reports', 'view_analytics'],
    'manage_billing': ['manage_billing'],
    'family_communication': ['family_communications', 'family_updates'],
    'emergency_override': ['emergency_override'],
    'system_settings': ['system_settings']
  };
  
  const requiredPermissions = actionPermissions[action] || [];
  return requiredPermissions.some(permission => hasRolePermission(user, permission));
};

// Get user's role display name
export const getRoleDisplayName = (userRole) => {
  const { rolePermissions } = require('../data/mockData');
  return rolePermissions[userRole]?.name || userRole;
};

// Get user's role description
export const getRoleDescription = (userRole) => {
  const { rolePermissions } = require('../data/mockData');
  return rolePermissions[userRole]?.description || '';
};

// Aged care specific helpers
export const getCareLevelColor = (careLevel) => {
  switch (careLevel) {
    case 'Critical': return 'text-red-600';
    case 'High': return 'text-orange-600';
    case 'Medium': return 'text-yellow-600';
    case 'Low': return 'text-green-600';
    default: return 'text-gray-600';
  }
};

export const getFallRiskColor = (fallRisk) => {
  switch (fallRisk) {
    case 'High': return 'text-red-600';
    case 'Medium': return 'text-yellow-600';
    case 'Low': return 'text-green-600';
    default: return 'text-gray-600';
  }
};

export const getMedicationStatusColor = (status) => {
  switch (status) {
    case 'Active': return 'text-green-600';
    case 'Expiring Soon': return 'text-orange-600';
    case 'Expired': return 'text-red-600';
    default: return 'text-gray-600';
  }
};

// Validation helpers
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePhone = (phone) => {
  const re = /^[+]?[1-9][\d]{0,15}$/;
  return re.test(phone.replace(/\s/g, ''));
};

export const validateMedicare = (medicare) => {
  const re = /^[0-9]{4}\s?[0-9]{5}\s?[0-9]{1}$/;
  return re.test(medicare.replace(/\s/g, ''));
};

// Performance helpers
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Local storage helpers
export const saveToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

// Network status helpers
export const checkNetworkStatus = () => {
  return navigator.onLine ? 'connected' : 'disconnected';
};

// Date helpers for aged care
export const getDaysSinceAdmission = (admissionDate) => {
  const admission = new Date(admissionDate);
  const today = new Date();
  const diffTime = Math.abs(today - admission);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const getNextAssessmentDate = (lastAssessment, frequency = 30) => {
  const last = new Date(lastAssessment);
  const next = new Date(last);
  next.setDate(next.getDate() + frequency);
  return next;
};

export const isAssessmentOverdue = (lastAssessment, frequency = 30) => {
  const nextAssessment = getNextAssessmentDate(lastAssessment, frequency);
  return new Date() > nextAssessment;
};

// Medication helpers
export const isPrescriptionExpiring = (expiryDate, daysWarning = 7) => {
  const expiry = new Date(expiryDate);
  const warningDate = new Date();
  warningDate.setDate(warningDate.getDate() + daysWarning);
  return expiry <= warningDate;
};

export const calculateAdherenceRate = (prescribed, taken) => {
  if (prescribed === 0) return 100;
  return Math.round((taken / prescribed) * 100);
};

// Export all helpers
const helpers = {
  formatCurrency,
  formatDate,
  formatTime,
  getTimeAgo,
  getCurrentFacility,
  getWardsByFacility,
  authenticateUser,
  hasPermission,
  canAccessFacility,
  canViewResident,
  getFilteredResidents,
  getFilteredAlerts,
  getAvailableTabs,
  getCareLevelColor,
  getFallRiskColor,
  getMedicationStatusColor,
  validateEmail,
  validatePhone,
  validateMedicare,
  debounce,
  throttle,
  saveToLocalStorage,
  getFromLocalStorage,
  checkNetworkStatus,
  getDaysSinceAdmission,
  getNextAssessmentDate,
  isAssessmentOverdue,
  isPrescriptionExpiring,
  calculateAdherenceRate,
  hasRolePermission,
  getRoleInfo,
  canPerformAction,
  getRoleDisplayName,
  getRoleDescription
};

export default helpers;
